import { getApiBaseUrl } from '@config/api';
import type { LiveHostState, LiveLinkErrorCode, LiveScreenRead, LiveState } from '@models/api';
import { noteServerDate } from '@lib/livePolls/serverClock';
import { getAuthToken } from './api-client';

// AIDEV-NOTE: the live reads polled every 2 s (TECH-867) are bare `fetch`es, not `customInstance`
// (CONTRACT § 11, contract check fix 4). Through the axios instance a read waits on the bearer behind the
// cross-tab token lock, is refused while a session is expired, and hands every 5xx or network blip of a room
// polling every 2 s to the maintenance detector, which can raise MaintenanceTakeover over the live poll
// instead of "Reconnecting…". Precedent: `useMagicLinkFlowContext`. Kept in its own module, beside the
// instance, so the live lane does not edit the shared `api-client.ts`.

/**
 * How a screen read went out: with the signed-in bearer, as a guest because there is no session, or as a guest
 * because the bearer missed `LIVE_TOKEN_BUDGET_MS` (a slow token refresh; the read then says non-author though
 * the author is signed in).
 */
export type LiveReadBearer = 'sent' | 'none' | 'late';

/** A read that answered: the data, or the link's 404 (E3: unknown code, wrong key, deleted, expired). */
export type LiveRead<T> =
  | { ok: true; data: T; bearer?: LiveReadBearer }
  | { ok: false; code: LiveLinkErrorCode };

/**
 * A read that did not answer (5xx, a refusal other than 404, a transport failure). Deliberately status-less
 * and not network-shaped (`httpStatus`, not `status`), so `queryCacheOnError` never forwards it to the
 * maintenance probe: the screen shows "Reconnecting…" (E9b) and keeps its last state instead.
 */
export class LiveReadError extends Error {
  readonly httpStatus: number | null;
  constructor(httpStatus: number | null) {
    super(httpStatus === null ? 'Live read got no answer' : `Live read answered ${httpStatus}`);
    this.name = 'LiveReadError';
    this.httpStatus = httpStatus;
    Object.setPrototypeOf(this, LiveReadError.prototype);
  }
}

/**
 * A read with no answer after this long is a failure (it counts toward "Reconnecting…"), not a hang: a fetch
 * that never settles would otherwise freeze the 2 s polling with the last state and no word.
 */
export const LIVE_READ_TIMEOUT_MS = 5_000;

/** The caller's signal (React Query's: unmount, a newer read) combined with the read's own timeout. */
const withTimeout = (signal: AbortSignal | undefined): { signal: AbortSignal; done: () => void } => {
  const controller = new AbortController();
  const timer = setTimeout(() => { controller.abort(); }, LIVE_READ_TIMEOUT_MS);
  const onAbort = () => { controller.abort(); };
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener('abort', onAbort, { once: true });
  return {
    signal: controller.signal,
    done: () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    },
  };
};

/** How long the screen waits for a signed-in bearer before reading as a guest (the key alone shows it). */
export const LIVE_TOKEN_BUDGET_MS = 2_000;

const LATE = Symbol('late');

const bearerWithinBudget = async (): Promise<{ token: string | null; late: boolean }> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const token = await Promise.race([
      getAuthToken().catch(() => null),
      new Promise<typeof LATE>(resolve => {
        timer = setTimeout(() => resolve(LATE), LIVE_TOKEN_BUDGET_MS);
      }),
    ]);
    return token === LATE ? { token: null, late: true } : { token, late: false };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

const linkErrorCode = async (response: Response): Promise<LiveLinkErrorCode> => {
  try {
    const body = (await response.json()) as { detail?: { error_code?: unknown } } | null;
    return body?.detail?.error_code === 'LIVE_LINK_EXPIRED' ? 'LIVE_LINK_EXPIRED' : 'LIVE_LINK_INVALID';
  } catch {
    return 'LIVE_LINK_INVALID';
  }
};

const readLive = async <T>(
  path: string,
  signal: AbortSignal | undefined,
  bearer: string | null,
  // `/state` sends none: `no-store` adds `Cache-Control: no-cache` to the request, which can make the 1 s edge
  // cache in the Pages Function go to the origin for every phone. `/screen` is per viewer and never cached.
  cache?: RequestCache,
): Promise<LiveRead<T>> => {
  let response: Response;
  const timed = withTimeout(signal);
  try {
    response = await fetch(`${getApiBaseUrl()}${path}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      },
      credentials: 'omit',
      ...(cache ? { cache } : {}),
      signal: timed.signal,
    });
  } catch (error) {
    // An abort by the caller is the caller leaving; React Query drops it. Anything else, the read's own
    // timeout included, is no answer.
    if (signal?.aborted) throw error;
    throw new LiveReadError(null);
  } finally {
    timed.done();
  }
  noteServerDate(response.headers.get('Date'));
  if (response.status === 404) return { ok: false, code: await linkErrorCode(response) };
  if (!response.ok) throw new LiveReadError(response.status);
  try {
    return { ok: true, data: (await response.json()) as T };
  } catch {
    // A 200 that is not JSON (an intercepting proxy's HTML page) is no answer either.
    throw new LiveReadError(response.status);
  }
};

/**
 * `GET /live/{code}/screen?key=` (CONTRACT § 4.15): auth optional, so the signed-in author's bearer rides
 * along when there is one (`viewer_is_author`); a projector that is signed out reads with the key alone.
 */
export const getLiveScreen = async (
  code: string,
  key: string,
  signal?: AbortSignal,
): Promise<LiveRead<LiveScreenRead>> => {
  const { token, late } = await bearerWithinBudget();
  const read = await readLive<LiveScreenRead>(
    `api/v1/live/${encodeURIComponent(code)}/screen?key=${encodeURIComponent(key)}`,
    signal,
    token,
    'no-store',
  );
  if (!read.ok) return read;
  return { ...read, bearer: token ? 'sent' : late ? 'late' : 'none' };
};

/** `GET /live/{code}/state` (CONTRACT § 4.10): public, never a bearer, so the edge cache keys on the URL. */
export const getLiveState = (code: string, signal?: AbortSignal): Promise<LiveRead<LiveState>> =>
  readLive<LiveState>(`api/v1/live/${encodeURIComponent(code)}/state`, signal, null);

/**
 * `GET /live/{code}/host` (CONTRACT § 4.15), the author's host read polled every 2 s by the host view and the
 * author's card (FE-L1b): a bare fetch for the same reasons as the reads above, with the bearer it needs. A 404
 * answers `ok: false` (E3); 403 `LIVE_NOT_AUTHOR` throws a `LiveReadError(403)`, also E3 for the caller.
 */
export const getLiveHostRead = async (code: string, signal?: AbortSignal): Promise<LiveRead<LiveHostState>> =>
  readLive<LiveHostState>(`api/v1/live/${encodeURIComponent(code)}/host`, signal, await getAuthToken(), 'no-store');
